import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faLockOpen, faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { apiClientFactory } from "helpers";
import { Alert as AppAlert } from "components/ui/alert";
import { Button as AppButton } from "components/ui/button";
import {
  Dialog as AppDialog,
  DialogContent as AppDialogContent,
  DialogFooter as AppDialogFooter,
  DialogHeader as AppDialogHeader,
  DialogTitle as AppDialogTitle,
} from "components/ui/dialog";
import {
  Tabs as AppTabs,
  TabsList as AppTabsList,
  TabsTrigger as AppTabsTrigger,
} from "components/ui/tabs";
import {
  AccessControlGroupResponse,
  AccessControlGroupType,
  ApiException,
  GuestResponse,
  OnboardCreateInviteRequest,
  OnboardGroupInfo,
  OnboardingPreviewResponse,
  OnboardPropertyInfo,
  OnboardUnitInfo,
  PropertyListResponse,
  SiteResponse,
  StaffOnboardingRequest,
} from "api/ApiClient";
import { GuestType, GuestResponse as GuestRecordResponse } from "components/AdminGuests/AdminGuests";
import { GuestPropertyAccessResponse } from "components/AdminGuests/GuestPropertyAccess";

function today() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function oneYearFromToday() {
  const date = today();
  date.setFullYear(date.getFullYear() + 1);
  return date;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addYears(date: Date, years: number): Date {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

// Validity is optional (preferred, but not required) throughout this modal, so dates are
// represented as `Date | null` and rendered as an empty `datetime-local` input when unset.
function formatDateTimeLocal(date: Date | null): string {
  return date ? moment(date).format("YYYY-MM-DDTHH:mm:ss") : "";
}

function parseDateTimeLocal(value: string): Date | null {
  return value ? new Date(moment(value).format("YYYY-MM-DDTHH:mm:ss")) : null;
}

// ACG types that indicate a group is meant for staff rather than residents.
const STAFF_ACG_TYPES = ["staffOnly", "defaultForStaff"];

// Loose fuzzy match: true if the target contains the query, or the query's
// characters all appear (in order) somewhere within the target.
function fuzzyMatch(query: string, target: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  if (t.includes(q)) return true;

  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

export type OnboardGuestType = "resident" | "staff";

interface GuestOnboardingModalProps {
  open: boolean;
  /** When provided, entries default to this site's property instead of requiring a manual search. */
  siteId?: string;
  /**
   * Legacy site-scoped update path: when provided, the modal pre-fills from this guest's
   * site-scoped access control groups and behaves as an update rather than a new onboarding.
   */
  guest?: GuestResponse;
  /**
   * Site-agnostic update path: pre-fills from the guest record and the full property-access
   * list (each entry already carries its own Groups/LeaseId/validity), bypassing ACG derivation
   * entirely. Takes priority over `guest` when both are provided.
   */
  guestRecord?: GuestRecordResponse;
  propertyAccesses?: GuestPropertyAccessResponse[];
  onClose: (submitted?: boolean) => void;
}

// A single onboarding entry: a property (and, for residents, a specific unit within it)
// paired with the access control groups granted there. Multiple entries can span
// different properties, mirroring the "units"/"properties" collections in the contract.
interface OnboardEntry {
  key: string;
  propertyExternalId: string;
  /** Empty string represents property-wide access rather than a specific unit. Unused for staff entries. */
  unitNumber: string;
  /** Optional lease identifier for resident entries. Unused for staff entries. */
  leaseId: string;
  /** ACG externalId values (the identifiers the backend expects as groupUniqueName). */
  groupExternalIds: string[];
  /** Each unit/property access entry carries its own validity window. Optional — preferred but not required. */
  validFrom: Date | null;
  validTo: Date | null;
}

function buildEntriesFromAcgs(
  acgs: AccessControlGroupResponse[],
  byUnit: boolean,
  fallbackValidFrom: Date | null,
  fallbackValidTo: Date | null,
): OnboardEntry[] {
  const map = new Map<string, OnboardEntry>();

  acgs.forEach((acg) => {
    const propertyExternalId = acg.propertyExternalId ?? "";
    const unitNumber = byUnit ? (acg.unitNumber ?? "") : "";
    const key = `${propertyExternalId}::${unitNumber}`;
    let entry = map.get(key);
    if (!entry) {
      entry = {
        key,
        propertyExternalId,
        unitNumber,
        leaseId: "",
        groupExternalIds: [],
        validFrom: fallbackValidFrom,
        validTo: fallbackValidTo,
      };
      map.set(key, entry);
    }
    // Server-provided entries without a real ACG (e.g. a unit/property with no additional
    // entryways beyond its default access) carry no externalId - they only exist to seed the
    // entry itself and must not be treated as a selected group.
    if (acg.externalId) {
      entry.groupExternalIds.push(acg.externalId);
    }
  });

  return Array.from(map.values());
}

// Site-agnostic hydration: each property-access record already carries its own LeaseId,
// validity window and additional-entryway Groups, so entries are built directly with no
// placeholder-entry reconstruction needed (unlike buildEntriesFromAcgs above).
function buildEntriesFromPropertyAccess(
  propertyAccesses: GuestPropertyAccessResponse[],
  guestType: OnboardGuestType,
  fallbackValidFrom: Date | null,
  fallbackValidTo: Date | null,
): OnboardEntry[] {
  const targetGuestType =
    guestType === "resident" ? GuestType.Resident : GuestType.Staff;

  return propertyAccesses
    .filter((pa) => !pa.deletedTimestamp && pa.guestType === targetGuestType)
    .map((pa) => {
      const unitNumber = guestType === "resident" ? (pa.unitNumber ?? "") : "";
      return {
        key: `${pa.propertyExternalId}::${unitNumber}`,
        propertyExternalId: pa.propertyExternalId,
        unitNumber,
        leaseId: pa.leaseId ?? "",
        groupExternalIds: (pa.groups ?? []).map((group) => group.groupUniqueName),
        validFrom: pa.validFrom ? new Date(pa.validFrom) : fallbackValidFrom,
        validTo: pa.validTo ? new Date(pa.validTo) : fallbackValidTo,
      };
    });
}

export default function GuestOnboardingModal({
  open,
  siteId,
  guest,
  guestRecord,
  propertyAccesses,
  onClose,
}: GuestOnboardingModalProps) {
  const isUpdate = !!guest || !!guestRecord;

  const [guestType, setGuestType] = useState<OnboardGuestType>("resident");
  const [submitting, setSubmitting] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sites, setSites] = useState<SiteResponse[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [properties, setProperties] = useState<PropertyListResponse[]>([]);
  const [id, setId] = useState("");
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [unitEntries, setUnitEntries] = useState<OnboardEntry[]>([]);
  const [propertyEntries, setPropertyEntries] = useState<OnboardEntry[]>([]);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<OnboardEntry | null>(null);
  const [validFrom, setValidFrom] = useState<Date | null>(today());
  const [validTo, setValidTo] = useState<Date | null>(oneYearFromToday());
  /** When true (the default), Valid From/To are calculated automatically from the entries below. */
  const [validityLocked, setValidityLocked] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] =
    useState<OnboardingPreviewResponse | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const entries = guestType === "resident" ? unitEntries : propertyEntries;
  const setEntries =
    guestType === "resident" ? setUnitEntries : setPropertyEntries;

  // Roll the top-level validity up/down to span every entry's own validity window.
  // Entries without a validity value are ignored; if none of the entries have one, the
  // rolled-up value is null (validity remains optional).
  const rolledUpValidity = useMemo(() => {
    if (entries.length === 0) return null;
    const froms = entries
      .map((entry) => entry.validFrom)
      .filter((d): d is Date => d !== null)
      .map((d) => d.getTime());
    const tos = entries
      .map((entry) => entry.validTo)
      .filter((d): d is Date => d !== null)
      .map((d) => d.getTime());
    return {
      validFrom: froms.length > 0 ? new Date(Math.min(...froms)) : null,
      validTo: tos.length > 0 ? new Date(Math.max(...tos)) : null,
    };
  }, [entries]);

  useEffect(() => {
    if (!validityLocked || !rolledUpValidity) return;
    setValidFrom(rolledUpValidity.validFrom);
    setValidTo(rolledUpValidity.validTo);
  }, [validityLocked, rolledUpValidity]);

  // Guards against wiping in-progress user edits: once the form has been initialized for
  // the current "open" session, subsequent reference changes to guest/guestRecord/propertyAccesses
  // (e.g. a react-query refetch while the modal stays open) should not reset the form. The one
  // exception is `propertyAccesses` resolving asynchronously *after* the modal was already open
  // (still `undefined` on first init) — in that case we re-hydrate once with the real data.
  const initializedRef = useRef(false);
  const hydratedFromPropertyAccessRef = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      hydratedFromPropertyAccessRef.current = false;
      return;
    }

    const propertyAccessesJustArrived =
      !hydratedFromPropertyAccessRef.current && !!propertyAccesses;
    if (initializedRef.current && !propertyAccessesJustArrived) return;

    initializedRef.current = true;
    if (propertyAccesses) hydratedFromPropertyAccessRef.current = true;

    const guestAcgs = guest?.accessControlGroups ?? [];
    const defaultGuestType: OnboardGuestType = propertyAccesses
      ? propertyAccesses.some(
          (pa) => !pa.deletedTimestamp && pa.guestType === GuestType.Resident,
        )
        ? "resident"
        : "staff"
      : guestAcgs.some((acg) => STAFF_ACG_TYPES.includes(acg.type as string))
        ? "staff"
        : "resident";

    setGuestType(defaultGuestType);
    setId(guestRecord?.id ?? guest?.id ?? "");
    setFirstName(guestRecord?.firstName ?? guest?.firstName ?? "");
    setLastName(guestRecord?.lastName ?? guest?.lastName ?? "");
    setEmail(guestRecord?.email ?? "");
const recordValidFrom = guestRecord ? guestRecord.validFrom : guest?.validFrom;
const recordValidTo = guestRecord ? guestRecord.validTo : guest?.validTo;
    // For an existing guest, an explicit null means indefinite/unrestricted access and must be
    // preserved; the today()/oneYearFromToday() defaults are only sensible for a brand-new guest.
    const fallbackValidFrom = recordValidFrom
      ? new Date(recordValidFrom)
      : isUpdate
        ? null
        : today();
    const fallbackValidTo = recordValidTo
      ? new Date(recordValidTo)
      : isUpdate
        ? null
        : oneYearFromToday();
    if (propertyAccesses) {
      setUnitEntries(
        buildEntriesFromPropertyAccess(
          propertyAccesses,
          "resident",
          fallbackValidFrom,
          fallbackValidTo,
        ),
      );
      setPropertyEntries(
        buildEntriesFromPropertyAccess(
          propertyAccesses,
          "staff",
          fallbackValidFrom,
          fallbackValidTo,
        ),
      );
    } else {
      setUnitEntries(
        buildEntriesFromAcgs(guestAcgs, true, fallbackValidFrom, fallbackValidTo),
      );
      setPropertyEntries(
        buildEntriesFromAcgs(
          guestAcgs,
          false,
          fallbackValidFrom,
          fallbackValidTo,
        ),
      );
    }
    setEntryDialogOpen(false);
    setEditingEntry(null);
    setError("");
    setValidFrom(fallbackValidFrom);
    setValidTo(fallbackValidTo);
    setValidityLocked(true);

    const loadSites = async () => {
      setSitesLoading(true);
      try {
        const siteList = await apiClientFactory().getSites(
          0,
          1000,
          undefined,
          undefined,
        );
        setSites(siteList ?? []);
      } catch {
        setError("Failed to load sites.");
      } finally {
        setSitesLoading(false);
      }
    };

    const loadProperties = async () => {
      setPropertiesLoading(true);
      try {
        const propertyList = await apiClientFactory().getProperties();
        setProperties(propertyList ?? []);
      } catch {
        setError("Failed to load properties.");
      } finally {
        setPropertiesLoading(false);
      }
    };

    loadSites();
    loadProperties();
  }, [open, siteId, guest, guestRecord, propertyAccesses]);

  const openAddEntry = () => {
    setEditingEntry(null);
    setEntryDialogOpen(true);
  };

  const openEditEntry = (entry: OnboardEntry) => {
    setEditingEntry(entry);
    setEntryDialogOpen(true);
  };

  const removeEntry = (key: string) => {
    setEntries(entries.filter((entry) => entry.key !== key));
  };

  const handleSaveEntry = (entry: OnboardEntry) => {
    const exists = entries.some((e) => e.key === entry.key);
    setEntries(
      exists
        ? entries.map((e) => (e.key === entry.key ? entry : e))
        : [...entries, entry],
    );
    setEntryDialogOpen(false);
    setEditingEntry(null);
  };

  // Entries already in the list, excluding whichever one is currently being edited (if any) —
  // passed down so the dialog can warn about adding the same property/unit twice.
  const existingEntriesForDialog = entries.filter(
    (e) => e.key !== editingEntry?.key,
  );

  // Shared between the real submit and the preview call, so preview can never drift from
  // what onboarding would actually send to the backend.
  const buildRequestBody = ():
    | { guestType: "resident"; body: OnboardCreateInviteRequest }
    | { guestType: "staff"; body: StaffOnboardingRequest } => {
    const newValidFrom = validFrom
      ? moment(validFrom).format("YYYY-MM-DDTHH:mm:ss")
      : undefined;
    const newValidTo = validTo
      ? moment(validTo).format("YYYY-MM-DDTHH:mm:ss")
      : undefined;
    const formatEntryValidity = (date: Date | null) =>
      date ? moment(date).format("YYYY-MM-DDTHH:mm:ss") : undefined;

    const groupsFor = (entry: OnboardEntry): OnboardGroupInfo[] =>
      entry.groupExternalIds.map((groupUniqueName) => ({ groupUniqueName }));

    if (guestType === "resident") {
      const units: OnboardUnitInfo[] = unitEntries.map((entry) => ({
        unitUniqueName: entry.unitNumber || undefined,
        buildingUniqueName: entry.propertyExternalId,
        leaseId: entry.leaseId || undefined,
        validFrom: formatEntryValidity(entry.validFrom),
        validTo: formatEntryValidity(entry.validTo),
        groups: groupsFor(entry),
      }));

      return {
        guestType: "resident",
        body: {
          guestId: id,
          guestEmail: email || undefined,
          guestFirstName: firstName,
          guestLastName: lastName,
          validFrom: newValidFrom,
          validTo: newValidTo,
          units,
        },
      };
    }

    const properties: OnboardPropertyInfo[] = propertyEntries.map(
      (entry) => ({
        propertyExternalId: entry.propertyExternalId,
        validFrom: formatEntryValidity(entry.validFrom),
        validTo: formatEntryValidity(entry.validTo),
        groups: groupsFor(entry),
      }),
    );

    return {
      guestType: "staff",
      body: {
        guestId: id,
        guestEmail: email || undefined,
        guestFirstName: firstName,
        guestLastName: lastName,
        validFrom: newValidFrom,
        validTo: newValidTo,
        properties,
      },
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const request = buildRequestBody();
      if (request.guestType === "resident") {
        await apiClientFactory().onboardResident(request.body);
      } else {
        await apiClientFactory().onboardStaff(request.body);
      }
      onClose(true);
    } catch (err) {
      const apiError = err as ApiException;
      setError(apiError?.message ?? "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreview = async () => {
    setError("");
    setPreviewLoading(true);
    try {
      const request = buildRequestBody();
      const result =
        request.guestType === "resident"
          ? await apiClientFactory().previewResident(request.body)
          : await apiClientFactory().previewStaff(request.body);
      setPreviewResult(result);
      setPreviewDialogOpen(true);
    } catch (err) {
      const apiError = err as ApiException;
      setError(apiError?.message ?? "An unexpected error occurred.");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <AppDialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <AppDialogHeader>
          <AppDialogTitle>
            {isUpdate ? "Update Onboarding" : "Onboard Guest"}
          </AppDialogTitle>
        </AppDialogHeader>
      <AppTabs
        value={guestType}
        onValueChange={(value) => setGuestType(value as OnboardGuestType)}
        aria-label="Guest type"
      >
        <AppTabsList className="px-3">
          <AppTabsTrigger value="resident">Resident</AppTabsTrigger>
          <AppTabsTrigger value="staff">Staff</AppTabsTrigger>
        </AppTabsList>
      </AppTabs>
      <form onSubmit={handleSubmit}>
        <div className="border-y py-4">
          {error && (
            <AppAlert className="mb-2" variant="destructive">
              {error}
            </AppAlert>
          )}
          {sitesLoading ? (
            <div>Loading...</div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="og-guestId">Id</label>
                <input
                  type="text"
                  id="og-guestId"
                  className="form-control"
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  disabled={isUpdate}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="og-email">Email</label>
                <input
                  type="email"
                  id="og-email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="og-firstName">First Name</label>
                <input
                  type="text"
                  id="og-firstName"
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="og-lastName">Last Name</label>
                <input
                  type="text"
                  id="og-lastName"
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <label style={{ marginBottom: 0 }}>Validity</label>
                  <AppButton
                    aria-label={
                      validityLocked
                        ? "Unlock to set validity manually"
                        : "Lock to calculate validity automatically"
                    }
                    onClick={() => setValidityLocked((locked) => !locked)}
                    size="icon-xs"
                    type="button"
                    variant="ghost"
                  >
                    <FontAwesomeIcon
                      icon={validityLocked ? faLock : faLockOpen}
                      fontSize="small"
                    />
                  </AppButton>
                </div>
                {validityLocked && (
                  <div style={{ fontSize: 12, color: "gray" }}>
                    Automatically calculated from each unit&apos;s validity.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="og-validFrom">Valid From</label>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input
                    type="datetime-local"
                    id="og-validFrom"
                    className="form-control"
                    value={formatDateTimeLocal(validFrom)}
                    disabled={validityLocked}
                    onChange={(e) =>
                      setValidFrom(parseDateTimeLocal(e.target.value))
                    }
                  />
                  {!validityLocked && validFrom && (
                    <AppButton
                      aria-label="Clear valid from"
                      onClick={() => setValidFrom(null)}
                      size="icon-xs"
                      type="button"
                      variant="ghost"
                    >
                      <FontAwesomeIcon icon={faTrash} fontSize="small" />
                    </AppButton>
                  )}
                </div>
                {!validityLocked && (
                  <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                    <AppButton size="sm" type="button" variant="ghost" onClick={() => setValidFrom(today())}>
                      Today
                    </AppButton>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="og-validTo">Valid To</label>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input
                    type="datetime-local"
                    id="og-validTo"
                    className="form-control"
                    value={formatDateTimeLocal(validTo)}
                    disabled={validityLocked}
                    onChange={(e) =>
                      setValidTo(parseDateTimeLocal(e.target.value))
                    }
                  />
                  {!validityLocked && validTo && (
                    <AppButton
                      aria-label="Clear valid to"
                      onClick={() => setValidTo(null)}
                      size="icon-xs"
                      type="button"
                      variant="ghost"
                    >
                      <FontAwesomeIcon icon={faTrash} fontSize="small" />
                    </AppButton>
                  )}
                </div>
                {!validityLocked && (
                  <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                    <AppButton
                      onClick={() =>
                        setValidTo(addDays(validFrom ?? today(), 30))
                      }
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      +30 Days
                    </AppButton>
                    <AppButton
                      onClick={() =>
                        setValidTo(addDays(validFrom ?? today(), 90))
                      }
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      +90 Days
                    </AppButton>
                    <AppButton
                      onClick={() =>
                        setValidTo(addYears(validFrom ?? today(), 1))
                      }
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      +1 Year
                    </AppButton>
                  </div>
                )}
              </div>
              <div className="form-group">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <label style={{ marginBottom: 0 }}>
                    {guestType === "resident" ? "Units" : "Property Access"}
                  </label>
                  <AppButton
                    onClick={openAddEntry}
                    disabled={submitting || sitesLoading || propertiesLoading}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    + Add{" "}
                    {guestType === "resident" ? "Property Access" : "Property"}
                  </AppButton>
                </div>
                {entries.length === 0 ? (
                  <div className="alert alert-warning" style={{ marginTop: 8 }}>
                    {guestType === "resident"
                      ? "No units added yet."
                      : "No properties added yet."}
                  </div>
                ) : (
                  entries.map((entry) => (
                    <div
                      key={entry.key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 8px",
                        border: "1px solid #ddd",
                        borderRadius: 4,
                        marginTop: 8,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {entry.propertyExternalId}
                        </div>
                        <div style={{ fontSize: 12, color: "gray" }}>
                          {guestType === "resident"
                            ? entry.unitNumber
                              ? `Unit ${entry.unitNumber}`
                              : "Property-Wide Access"
                            : "All Property Access"}
                          {guestType === "resident" && entry.leaseId
                            ? ` \u00b7 Lease ${entry.leaseId}`
                            : ""}
                          {" \u00b7 "}
                          {entry.groupExternalIds.length} group
                          {entry.groupExternalIds.length === 1 ? "" : "s"}
                          {(entry.validFrom || entry.validTo) && (
                            <>
                              {" \u00b7 "}
                              {entry.validFrom
                                ? moment(entry.validFrom).format("MMM D, YYYY")
                                : "No start date"}
                              {" \u2013 "}
                              {entry.validTo
                                ? moment(entry.validTo).format("MMM D, YYYY")
                                : "No end date"}
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <AppButton size="sm" type="button" variant="ghost" onClick={() => openEditEntry(entry)}>
                          Edit
                        </AppButton>
                        <AppButton
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => removeEntry(entry.key)}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          Remove
                        </AppButton>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        <AppDialogFooter>
          <AppButton onClick={() => onClose()} disabled={submitting} type="button" variant="outline">
            Cancel
          </AppButton>
          <AppButton
            onClick={handlePreview}
            disabled={
              entries.length === 0 ||
              id.length === 0 ||
              submitting ||
              previewLoading
            }
            type="button"
            variant="outline"
          >
            {previewLoading ? "Loading Preview..." : "Preview Access"}
          </AppButton>
          <AppButton
            type="submit"
            disabled={
              entries.length === 0 || id.length === 0 || submitting
            }
          >
            {submitting ? "Saving..." : isUpdate ? "Update" : "Onboard"}
          </AppButton>
        </AppDialogFooter>
      </form>
      <AddEntryDialog
        open={entryDialogOpen}
        guestType={guestType}
        fixedSiteId={siteId}
        sites={sites}
        properties={properties}
        initialEntry={editingEntry}
        existingEntries={existingEntriesForDialog}
        defaultValidFrom={validFrom}
        defaultValidTo={validTo}
        onCancel={() => {
          setEntryDialogOpen(false);
          setEditingEntry(null);
        }}
        onSave={handleSaveEntry}
      />
      <PreviewResultsDialog
        open={previewDialogOpen}
        result={previewResult}
        onClose={() => setPreviewDialogOpen(false)}
      />
      </AppDialogContent>
    </AppDialog>
  );
}

function PreviewResultsDialog({
  open,
  result,
  onClose,
}: {
  open: boolean;
  result: OnboardingPreviewResponse | null;
  onClose: () => void;
}) {
  const sites = result?.sites ?? [];

  return (
    <AppDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <AppDialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <AppDialogHeader>
          <AppDialogTitle>Access Preview</AppDialogTitle>
        </AppDialogHeader>
        {sites.length === 0 ? (
          <div className="alert alert-warning">
            No access control groups would be granted.
          </div>
        ) : (
          sites.map((site, siteIndex) => (
            <div key={site.siteId ?? siteIndex} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600 }}>{site.siteName}</div>
              {(site.accessControlGroups ?? []).map((acg, acgIndex) => (
                <div
                  key={acg.accessControlGroupId ?? acgIndex}
                  style={{
                    padding: "6px 8px",
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    marginTop: 8,
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{acg.name}</div>
                  <div style={{ fontSize: 12, color: "gray" }}>
                    {acg.type}
                    {(acg.validFrom || acg.validTo) && (
                      <>
                        {" \u00b7 "}
                        {acg.validFrom
                          ? moment(acg.validFrom).format("MMM D, YYYY")
                          : "No start date"}
                        {" \u2013 "}
                        {acg.validTo
                          ? moment(acg.validTo).format("MMM D, YYYY")
                          : "No end date"}
                      </>
                    )}
                    {acg.lockNames && acg.lockNames.length > 0 && (
                      <>
                        {" \u00b7 "}
                        {acg.lockNames.join(", ")}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <AppDialogFooter>
          <AppButton onClick={onClose} type="button" variant="outline">
            Close
          </AppButton>
        </AppDialogFooter>
      </AppDialogContent>
    </AppDialog>
  );
}

function AddEntryDialog({
  open,
  guestType,
  fixedSiteId,
  sites,
  properties,
  initialEntry,
  existingEntries,
  defaultValidFrom,
  defaultValidTo,
  onCancel,
  onSave,
}: {
  open: boolean;
  guestType: OnboardGuestType;
  fixedSiteId?: string;
  sites: SiteResponse[];
  properties: PropertyListResponse[];
  initialEntry: OnboardEntry | null;
  /** Entries already saved elsewhere in the list, used to guard against adding the same property/unit twice. */
  existingEntries: OnboardEntry[];
  defaultValidFrom: Date | null;
  defaultValidTo: Date | null;
  onCancel: () => void;
  onSave: (entry: OnboardEntry) => void;
}) {
  // Fuzzy-search for a property, then (for residents) a free-text unit number/lease id.
  // Residents only see "regular" access control groups (additional entryways); staff see
  // every access control group type available on the property.
  const [propertyQuery, setPropertyQuery] = useState("");
  const [selectedPropertyExternalId, setSelectedPropertyExternalId] =
    useState("");
  const [showPropertySuggestions, setShowPropertySuggestions] =
    useState(false);
  const [unitNumber, setUnitNumber] = useState("");
  const [leaseId, setLeaseId] = useState("");
  const [entryValidFrom, setEntryValidFrom] = useState<Date | null>(today());
  const [entryValidTo, setEntryValidTo] = useState<Date | null>(
    oneYearFromToday(),
  );
  const [residentGroupExternalIds, setResidentGroupExternalIds] = useState<
    string[]
  >([]);
  const [staffGroupExternalIds, setStaffGroupExternalIds] = useState<
    string[]
  >([]);
  const [availableAcgs, setAvailableAcgs] = useState<
    AccessControlGroupResponse[]
  >([]);
  const [acgsLoading, setAcgsLoading] = useState(false);
  const [acgError, setAcgError] = useState("");

  const groupExternalIds =
    guestType === "resident" ? residentGroupExternalIds : staffGroupExternalIds;
  const setGroupExternalIds =
    guestType === "resident"
      ? setResidentGroupExternalIds
      : setStaffGroupExternalIds;

  const propertySuggestions = useMemo(() => {
    if (!propertyQuery) return [];
    return properties
      .filter(
        (p) =>
          fuzzyMatch(propertyQuery, p.propertyExternalId ?? "") ||
          fuzzyMatch(propertyQuery, p.name ?? ""),
      )
      .slice(0, 8);
  }, [propertyQuery, properties]);

  useEffect(() => {
    if (!open) return;

    let initialPropertyExternalId = initialEntry?.propertyExternalId ?? "";
    if (fixedSiteId) {
      const fixedSite = sites.find((s) => s.id === fixedSiteId);
      const fixedProperty = properties.find((p) =>
        p.sites?.includes(fixedSite?.systemSiteId ?? "\u0000"),
      );
      initialPropertyExternalId = fixedProperty?.propertyExternalId ?? "";
    }

    const initialProperty = properties.find(
      (p) => p.propertyExternalId === initialPropertyExternalId,
    );
    setSelectedPropertyExternalId(initialPropertyExternalId);
    setPropertyQuery(
      initialProperty
        ? `${initialProperty.name ?? initialProperty.propertyExternalId} (${initialProperty.propertyExternalId})`
        : initialPropertyExternalId,
    );
    setUnitNumber(initialEntry?.unitNumber ?? "");
    setLeaseId(initialEntry?.leaseId ?? "");
    // initialEntry's validFrom/validTo may be an explicit null (indefinite) when editing an
    // existing entry, which must be preserved rather than replaced by the dialog's defaults —
    // those defaults only apply when there's no initialEntry at all (adding a brand-new entry).
    setEntryValidFrom(initialEntry ? initialEntry.validFrom : defaultValidFrom);
    setEntryValidTo(initialEntry ? initialEntry.validTo : defaultValidTo);
    setResidentGroupExternalIds(
      guestType === "resident" ? (initialEntry?.groupExternalIds ?? []) : [],
    );
    setStaffGroupExternalIds(
      guestType === "staff" ? (initialEntry?.groupExternalIds ?? []) : [],
    );
  }, [
    open,
    guestType,
    initialEntry,
    fixedSiteId,
    sites,
    properties,
    defaultValidFrom,
    defaultValidTo,
  ]);

  useEffect(() => {
    if (!open || !selectedPropertyExternalId) {
      setAvailableAcgs([]);
      setAcgError("");
      return;
    }

    let cancelled = false;
    const loadAcgs = async () => {
      setAcgsLoading(true);
      setAcgError("");
      try {
        const acgs = await apiClientFactory().getAccessControlGroups(
          0,
          1000,
          undefined,
          undefined,
          selectedPropertyExternalId,
        );
        const filtered =
          guestType === "resident"
            ? (acgs ?? []).filter(
                (acg) => acg.type === AccessControlGroupType.Regular,
              )
            : (acgs ?? []).filter(
                // defaultForStaff is always granted to staff, so only staffOnly is selectable.
                (acg) => acg.type === AccessControlGroupType.StaffOnly,
              );
        if (!cancelled) setAvailableAcgs(filtered);
      } catch {
        if (!cancelled) {
          setAvailableAcgs([]);
          setAcgError("Failed to load access control groups.");
        }
      } finally {
        if (!cancelled) setAcgsLoading(false);
      }
    };

    loadAcgs();
    return () => {
      cancelled = true;
    };
  }, [open, guestType, selectedPropertyExternalId]);

  const handleSave = () => {
    onSave({
      key: initialEntry?.key ?? `${selectedPropertyExternalId}::${Date.now()}`,
      propertyExternalId: selectedPropertyExternalId,
      unitNumber: guestType === "resident" ? unitNumber : "",
      leaseId: guestType === "resident" ? leaseId : "",
      groupExternalIds,
      validFrom: entryValidFrom,
      validTo: entryValidTo,
    });
  };

  // ACGs without an externalId can't be represented as a groupUniqueName, so they can't be
  // meaningfully selected — exclude them rather than letting them collapse onto one checkbox.
  const selectableAcgs = availableAcgs.filter((acg) => !!acg.externalId);

  const isDuplicateEntry = existingEntries.some(
    (e) =>
      e.propertyExternalId === selectedPropertyExternalId &&
      (guestType === "staff" || e.unitNumber === unitNumber),
  );

  const saveDisabled =
    !selectedPropertyExternalId ||
    isDuplicateEntry;

  return (
    <AppDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onCancel();
        }
      }}
    >
      <AppDialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-xs"
      >
        <AppDialogHeader>
          <AppDialogTitle>
            {guestType === "resident"
              ? initialEntry
                ? "Edit Unit"
                : "Add Unit"
              : initialEntry
                ? "Edit Property Access"
                : "Add Property Access"}
          </AppDialogTitle>
        </AppDialogHeader>
      <div className="border-y py-4">
        <div className="form-group" style={{ position: "relative" }}>
          <label htmlFor="entry-property">Property</label>
          {selectedPropertyExternalId ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 10px",
                border: "1px solid #ccc",
                borderRadius: 4,
                background: "#f5f5f5",
              }}
            >
              <span>{propertyQuery}</span>
              {!fixedSiteId && (
                <AppButton
                  aria-label="clear selected property"
                  onClick={() => {
                    setSelectedPropertyExternalId("");
                    setPropertyQuery("");
                    setGroupExternalIds([]);
                  }}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <FontAwesomeIcon icon={faTrash} fontSize="small" />
                </AppButton>
              )}
            </div>
          ) : (
            <>
              <input
                type="text"
                id="entry-property"
                className="form-control"
                value={propertyQuery}
                onChange={(e) => {
                  setPropertyQuery(e.target.value);
                  setSelectedPropertyExternalId("");
                  setGroupExternalIds([]);
                  setShowPropertySuggestions(true);
                }}
                onFocus={() => setShowPropertySuggestions(true)}
                onBlur={() =>
                  window.setTimeout(
                    () => setShowPropertySuggestions(false),
                    150,
                  )
                }
                placeholder="Search by property name or ID..."
                disabled={!!fixedSiteId}
                autoComplete="off"
              />
              {showPropertySuggestions && propertySuggestions.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    zIndex: 10,
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: 4,
                    width: "100%",
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {propertySuggestions.map((p) => (
                    <li
                      key={p.propertyExternalId}
                      style={{ padding: "6px 8px", cursor: "pointer" }}
                      onMouseDown={() => {
                        setSelectedPropertyExternalId(
                          p.propertyExternalId ?? "",
                        );
                        setPropertyQuery(
                          `${p.name ?? p.propertyExternalId} (${p.propertyExternalId})`,
                        );
                        setGroupExternalIds([]);
                        setShowPropertySuggestions(false);
                      }}
                    >
                      {p.name ?? p.propertyExternalId}{" "}
                      <span style={{ fontSize: 11, color: "gray" }}>
                        ({p.propertyExternalId})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        {isDuplicateEntry && (
          <div className="alert alert-warning">
            {guestType === "resident"
              ? "This property/unit is already in the list. Edit the existing entry instead of adding a duplicate."
              : "This property is already in the list. Edit the existing entry instead of adding a duplicate."}
          </div>
        )}
        <div className="form-group">
          <label htmlFor="entry-validFrom">Valid From</label>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input
              type="datetime-local"
              id="entry-validFrom"
              className="form-control"
              value={formatDateTimeLocal(entryValidFrom)}
              onChange={(e) =>
                setEntryValidFrom(parseDateTimeLocal(e.target.value))
              }
            />
            {entryValidFrom && (
              <AppButton
                aria-label="Clear valid from"
                onClick={() => setEntryValidFrom(null)}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <FontAwesomeIcon icon={faTrash} fontSize="small" />
              </AppButton>
            )}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
            <AppButton size="sm" type="button" variant="ghost" onClick={() => setEntryValidFrom(today())}>
              Today
            </AppButton>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="entry-validTo">Valid To</label>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input
              type="datetime-local"
              id="entry-validTo"
              className="form-control"
              value={formatDateTimeLocal(entryValidTo)}
              onChange={(e) =>
                setEntryValidTo(parseDateTimeLocal(e.target.value))
              }
            />
            {entryValidTo && (
              <AppButton
                aria-label="Clear valid to"
                onClick={() => setEntryValidTo(null)}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <FontAwesomeIcon icon={faTrash} fontSize="small" />
              </AppButton>
            )}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
            <AppButton
              onClick={() =>
                setEntryValidTo(addDays(entryValidFrom ?? today(), 30))
              }
              size="sm"
              type="button"
              variant="ghost"
            >
              +30 Days
            </AppButton>
            <AppButton
              onClick={() =>
                setEntryValidTo(addDays(entryValidFrom ?? today(), 90))
              }
              size="sm"
              type="button"
              variant="ghost"
            >
              +90 Days
            </AppButton>
            <AppButton
              onClick={() =>
                setEntryValidTo(addYears(entryValidFrom ?? today(), 1))
              }
              size="sm"
              type="button"
              variant="ghost"
            >
              +1 Year
            </AppButton>
          </div>
        </div>
        {guestType === "resident" && (
          <>
            <div className="form-group">
              <label htmlFor="entry-unit">Unit Number</label>
              <input
                type="text"
                id="entry-unit"
                className="form-control"
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label htmlFor="entry-lease">Lease ID</label>
              <input
                type="text"
                id="entry-lease"
                className="form-control"
                value={leaseId}
                onChange={(e) => setLeaseId(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </>
        )}
        <div className="form-group">
          <label>Access Control Groups</label>
          {!selectedPropertyExternalId ? (
            <div className="alert alert-warning">
              Select a property to see its access control groups.
            </div>
          ) : acgsLoading ? (
            <div>Loading access control groups...</div>
          ) : acgError ? (
            <div className="alert alert-danger">{acgError}</div>
          ) : selectableAcgs.length === 0 ? (
            <div className="alert alert-warning">
              No access control groups found.
            </div>
          ) : (
            selectableAcgs.map((acg) => (
              <div key={acg.externalId} className="checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={groupExternalIds.includes(acg.externalId ?? "")}
                    onChange={(e) =>
                      e.target.checked
                        ? setGroupExternalIds([
                            ...groupExternalIds,
                            acg.externalId ?? "",
                          ])
                        : setGroupExternalIds(
                            groupExternalIds.filter(
                              (g) => g !== acg.externalId,
                            ),
                          )
                    }
                  />{" "}
                  {acg.name}
                  {guestType === "staff" && (
                    <span style={{ fontSize: 11, color: "gray" }}>
                      {" "}
                      ({acg.type})
                    </span>
                  )}
                </label>
              </div>
            ))
          )}
        </div>
      </div>
      <AppDialogFooter>
        <AppButton onClick={onCancel} type="button" variant="outline">
          Cancel
        </AppButton>
        <AppButton
          onClick={handleSave}
          disabled={saveDisabled}
          type="button"
        >
          Save
        </AppButton>
      </AppDialogFooter>
      </AppDialogContent>
    </AppDialog>
  );
}


